import { useQuery } from '@tanstack/react-query';
import type { AiModelCreditPrice } from '@troia-v3/types';

import { api } from '@/lib/api';

/**
 * Preço de cada modelo de IA em créditos deste app, por 1M de tokens.
 *
 * # Por que vem do servidor
 *
 * O catálogo `AI_MODELS` (que o frontend já tem, via pacote de tipos) traz o
 * preço em dólar do fabricante — é o custo NOSSO. O que o cliente paga sai da
 * tabela de custos do app, que é por tenant e editável no admin. Calcular
 * aqui pelo dólar mostraria um número que não é o da fatura de ninguém.
 *
 * Cache longo de propósito: preço de modelo muda quando alguém edita a tabela
 * no admin, não a cada minuto.
 */
export const useModelCredits = () => {
  return useQuery<AiModelCreditPrice[], Error>({
    queryKey: ['ai-model-credits'],
    queryFn: async () => {
      const response = await api.get('/credits/ai-models');
      return response.data.data as AiModelCreditPrice[];
    },
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Formata o preço para o selo: `12 mil` em vez de `12.000`, porque a coluna
 * é estreita e o que importa na comparação é a ordem de grandeza. Abaixo de
 * mil mostra o número cheio.
 */
export function formatarCreditos(valor: number): string {
  if (valor >= 1_000_000) {
    const milhoes = valor / 1_000_000;
    return `${milhoes.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;
  }
  if (valor >= 1_000) {
    const milhares = valor / 1_000;
    return `${milhares.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`;
  }
  return valor.toLocaleString('pt-BR');
}
